import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Message, ChatSession } from '@/types/chat';
import { useChatApi } from '@/hooks/useChatApi';
import { ChatApiService } from '@/services/chatApiService';
import { ERROR_CODES } from '@/lib/dify/errorUtils';
import { MessageHydrationAdapterV3 } from '@/lib/message-hydration';

export function useChatMessages({
  activeChat,
  userId,
  chatApiService,
  setChatSessions,
  updateChatSession,
}: {
  activeChat: ChatSession | undefined;
  userId: string | null;
  chatApiService: ChatApiService;
  setChatSessions: React.Dispatch<React.SetStateAction<ChatSession[]>>;
  updateChatSession: (chatId: string, updates: Partial<ChatSession>) => void;
}) {
  const chatApi = useChatApi(chatApiService);
  const { t } = useTranslation();
  const [messageVisibility, setMessageVisibility] = useState<Record<string, boolean>>({});

  // Create adapter once per hook instance, reusing across all operations
  const adapter = useMemo(() => new MessageHydrationAdapterV3(userId ?? ''), [userId]);

  const dedupeAssistantMessages = useCallback((messages: Message[]): Message[] => {
    if (!messages || messages.length === 0) return messages;
    const result: Message[] = [];
    for (let i = 0; i < messages.length; i++) {
      const current = messages[i];
      const previous = result[result.length - 1];

      // Use adapter for structural equality check
      const isDuplicateAssistant =
        previous &&
        previous.role === 'assistant' &&
        current.role === 'assistant' &&
        adapter.equals(previous, current);

      if (isDuplicateAssistant) {
        continue;
      }
      result.push(current);
    }
    return result;
  }, [adapter]);

  const shouldHideAssistantMessage = useCallback((message: Message): boolean => {
    if (message.role !== 'assistant') return false;
    const content = typeof message.content === 'string' ? message.content.trim() : '';
    if (!content) return false;
    const noisyLine = 'I will get creator profile stats and recent posts for accounts aligned with your themes.';
    return content === noisyLine;
  }, []);

  // --- CACHE ---
  const loadMessagesFromCache = useCallback((chatId: string, difyConvId: string | undefined): Message[] | null => {
    if (typeof window === 'undefined' || !userId) return null;

    const cacheKey = difyConvId ? `dify-messages-${difyConvId}-${userId}` : `dify-messages-${chatId}-${userId}`;
    const cachedMessagesString = localStorage.getItem(cacheKey);

    if (cachedMessagesString) {
      try {
        const parsedMessages = JSON.parse(cachedMessagesString);
        if (!Array.isArray(parsedMessages)) {
          localStorage.removeItem(cacheKey);
          return null;
        }

        // Use adapter to deserialize and auto-migrate
        return parsedMessages
          .map(msg => adapter.fromCache(msg))
          .filter((msg): msg is Message => msg !== null);
      } catch (e) {
        console.error(`Error parsing cached messages from key ${cacheKey}:`, e);
        localStorage.removeItem(cacheKey);
      }
    }
    return null;
  }, [userId]);

  const saveMessagesToCache = useCallback((chatId: string, difyConvId: string | undefined, messages: Message[]) => {
    if (typeof window === 'undefined' || !userId || !messages) return;
    const cacheKey = difyConvId ? `dify-messages-${difyConvId}-${userId}` : `dify-messages-${chatId}-${userId}`;
    try {
      // Use adapter to serialize
      const messagesToCache = messages.map(msg => adapter.toCache(msg));
      const serialized = JSON.stringify(messagesToCache);

      // Guard against oversized payloads
      const MAX_CACHE_SIZE = 300_000;
      if (serialized.length > MAX_CACHE_SIZE) {
        console.warn(`Skipping caching messages for key ${cacheKey} due to size: ${serialized.length} bytes, messages: ${messages.length}`);
        return;
      }

      localStorage.setItem(cacheKey, serialized);

      // Feedback remains in separate keys (adapter handles loading)
      messages.forEach(msg => {
        if (msg.feedback) {
          localStorage.setItem(`feedback-${msg.id}-${userId}`, msg.feedback);
        } else {
          localStorage.removeItem(`feedback-${msg.id}-${userId}`);
        }
      });
    } catch (e) {
      console.error(`Error saving messages to cache key ${cacheKey}:`, e);
    }
  }, [userId]);

  const clearStaleConversationCache = useCallback((chatId: string, difyConvId: string | undefined, messages: Message[]) => {
    if (typeof window === 'undefined' || !userId) {
      return;
    }

    if (difyConvId) {
      localStorage.removeItem(`dify-messages-${difyConvId}-${userId}`);
      localStorage.removeItem(`message-visibility-${difyConvId}-${userId}`);
    }

    saveMessagesToCache(chatId, undefined, messages);
  }, [saveMessagesToCache, userId]);

  const loadVisibilityFromCache = useCallback((difyConvId: string): Record<string, boolean> => {
    if (typeof window === 'undefined' || !userId || !difyConvId) return {};
    const visibilityCacheKey = `message-visibility-${difyConvId}-${userId}`;
    const cached = localStorage.getItem(visibilityCacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error) {
        localStorage.removeItem(visibilityCacheKey);
      }
    }
    return {};
  }, [userId]);

  const saveVisibilityToCache = useCallback((difyConvId: string, visibility: Record<string, boolean>) => {
    if (typeof window === 'undefined' || !userId || !difyConvId) return;
    const visibilityCacheKey = `message-visibility-${difyConvId}-${userId}`;
    try {
      localStorage.setItem(visibilityCacheKey, JSON.stringify(visibility));
    } catch (error) {
      console.error("Error saving message visibility to cache:", error);
    }
  }, [userId]);

  // --- MESSAGE LOADING EFFECT ---
  useEffect(() => {
    const loadMessages = async () => {
      if (!activeChat?.id || !userId) return;

      // When switching chats, reset visibility
      if (activeChat.difyConversationId) {
        setMessageVisibility(loadVisibilityFromCache(activeChat.difyConversationId));
      } else {
        setMessageVisibility({});
      }

      const existingMessageCount = activeChat.messages.length;
      const cachedMessages = loadMessagesFromCache(activeChat.id, activeChat.difyConversationId);

      // If the last cached message is an empty assistant message, the stream was interrupted
      // before completion. Skip the cache early-return so we can recover the full answer from
      // Dify's conversation history (requires difyConversationId to have been persisted).
      const lastCachedMsg = cachedMessages?.[cachedMessages.length - 1];
      const needsRecovery =
        !!activeChat.difyConversationId &&
        lastCachedMsg?.role === 'assistant' &&
        !lastCachedMsg.content?.trim();

      if (cachedMessages) {
        const cachedCount = cachedMessages.length;
        const shouldReplaceWithCache = cachedCount > 0 || existingMessageCount === 0;
        if (shouldReplaceWithCache && existingMessageCount !== cachedCount) {
          updateChatSession(activeChat.id, { messages: cachedMessages });
        }
        if (shouldReplaceWithCache && !needsRecovery) {
          return;
        }
      }

      if (activeChat.difyConversationId) {
        try {
          const history = await chatApi.fetchHistory(activeChat.difyConversationId, userId);
          if (history.missingConversation) {
            const fallbackMessages = cachedMessages ?? activeChat.messages ?? [];
            clearStaleConversationCache(activeChat.id, activeChat.difyConversationId, fallbackMessages);
            updateChatSession(activeChat.id, {
              difyConversationId: undefined,
              messages: existingMessageCount === 0 ? fallbackMessages : activeChat.messages,
            });
            return;
          }

          if (history.data && history.data.length > 0) {
            // Use adapter to deserialize history
            const messages: Message[] = history.data.flatMap(msg => adapter.fromHistory(msg));

            const shouldReplaceWithHistory = needsRecovery
              ? messages.length >= (cachedMessages?.length ?? 0)
              : existingMessageCount === 0;

            if (shouldReplaceWithHistory) {
              updateChatSession(activeChat.id, { messages });
              saveMessagesToCache(activeChat.id, activeChat.difyConversationId, messages);
            } else if (needsRecovery && lastCachedMsg?.id) {
              updateOptimisticMessage(activeChat.id, lastCachedMsg.id, {
                isDifyAgentError: true,
                errorMessage: t(ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE),
                errorCode: ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE,
              });
            }
          } else if (existingMessageCount === 0 && !needsRecovery) {
            // Only reset to empty when not in recovery mode — during recovery we keep
            // the cached state (with warning card) even if history returns nothing yet.
            updateChatSession(activeChat.id, { messages: [] });
            saveMessagesToCache(activeChat.id, activeChat.difyConversationId, []);
          } else if (needsRecovery && lastCachedMsg?.id) {
            // History returned nothing yet — Dify is still processing.
            // Flag the empty assistant message so the yellow warning card renders.
            updateOptimisticMessage(activeChat.id, lastCachedMsg.id, {
              isDifyAgentError: true,
              errorMessage: t(ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE),
              errorCode: ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE,
            });
          }
        } catch (error) {
          console.error(`Error fetching messages for chat ${activeChat.id}:`, error);
        }
      }
    };

    loadMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id, activeChat?.difyConversationId, userId, adapter]);

  // --- OPTIMISTIC UPDATE HELPERS ---
  const addOptimisticMessage = useCallback((chatId: string, message: Message) => {
    setChatSessions(prev =>
      prev.map(chat => {
        if (chat.id === chatId) {
          if (chat.messages.some(m => m.id === message.id)) {
            return chat;
          }
          return { ...chat, messages: [...chat.messages, message], updatedAt: new Date() };
        }
        return chat;
      })
    );
  }, [setChatSessions]);

  const updateOptimisticMessage = useCallback((chatId: string, messageId: string, updates: Partial<Message>) => {
    setChatSessions(prev =>
      prev.map(chat => {
        if (chat.id !== chatId) return chat;
        const updatedMessages = chat.messages.map(msg =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        );
        return { ...chat, messages: updatedMessages, updatedAt: new Date() };
      })
    );
  }, [setChatSessions]);

  const removeMessagesAfterIndex = useCallback((chatId: string, messageIndex: number) => {
    setChatSessions(prev =>
      prev.map(chat => {
        if (chat.id === chatId && messageIndex >= 0 && messageIndex < chat.messages.length - 1) {
          const truncatedMessages = chat.messages.slice(0, messageIndex + 1);
          return { ...chat, messages: truncatedMessages, updatedAt: new Date() };
        }
        return chat;
      })
    );
  }, [setChatSessions]);

  const toggleMessageVisibility = useCallback((messageId: string) => {
    setMessageVisibility(prev => {
      const newVisibility = { ...prev, [messageId]: !prev[messageId] };
      if (activeChat?.difyConversationId) {
        saveVisibilityToCache(activeChat.difyConversationId, newVisibility);
      }
      return newVisibility;
    });
  }, [activeChat, saveVisibilityToCache]);

  const visibleMessages = useMemo(
    () => (
      activeChat?.messages
        ? dedupeAssistantMessages(activeChat.messages).filter(msg => !shouldHideAssistantMessage(msg))
        : []
    ),
    [activeChat?.messages, dedupeAssistantMessages, shouldHideAssistantMessage]
  );

  return {
    messages: visibleMessages,
    messageVisibility,
    toggleMessageVisibility,
    addOptimisticMessage,
    updateOptimisticMessage,
    removeMessagesAfterIndex,
    saveMessagesToCache,
  };
}
